/* ==========================================================================
   Spin the wheel — checkout hand-off, page-independent
   ==========================================================================
   Loaded from layout/theme.liquid on EVERY page, unlike assets/spin-wheel.js
   which only loads where the wheel section renders.

   Why this file exists: the customer spins on one page, then browses. They add
   to cart on a product page, open the UpCart drawer there and click checkout.
   That page has no wheel, so spin-wheel.js is not on it, so the listener that
   turns the won code into a discount was never bound and the code was silently
   lost. The win itself is written by spin-wheel.js into sessionStorage; reading
   it back and acting on it belongs here, where it runs everywhere.

   Owns the whole hand-off: spin-wheel.js no longer binds any of this. Keep it
   that way — two listeners would both fire on the wheel page.
   ========================================================================== */
(function () {
  "use strict";

  if (window.__tdWheelHandoffLoaded) return;
  window.__tdWheelHandoffLoaded = true;

  var STORE_KEY = "wheel_discount_code"; // written by assets/spin-wheel.js
  // Native theme drawer first, then the UpCart builds. The theme drawer's button
  // is class `cart__checkout-button`, which the `checkoutButton` substring match
  // below does NOT cover (different casing/separator), so it needs its own entry.
  var SELECTORS =
    "#CartDrawer-Checkout, .cart__checkout-button, " +
    ".upcart-checkout-button, [class*=\"checkoutButton\"], [class*=\"upcart-checkout\"]";

  // ---- Market-aware URLs ----------------------------------------------------
  // Same implementation as spin-wheel.js: keeps /discount and /checkout inside
  // the current market so checkout locale and pricing stay correct. A bare
  // /discount/CODE?redirect=/checkout loses the market prefix and breaks routing.
  var shopRoot =
    window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : "/";
  if (shopRoot.charAt(shopRoot.length - 1) !== "/") shopRoot += "/";

  function marketPath(p) {
    if (!p) return shopRoot;
    if (p.charAt(0) === "#") return p;
    if (p.charAt(0) !== "/") return p;
    if (p.indexOf(shopRoot) === 0) return p;
    return shopRoot + p.replace(/^\//, "");
  }

  function withParams(url) {
    var qs = window.location.search || "";
    if (!qs) return url;
    return url + (url.indexOf("?") > -1 ? "&" : "?") + qs.replace(/^\?/, "");
  }

  // ---- Stored win -----------------------------------------------------------
  // sessionStorage survives navigation inside the tab, which is exactly the
  // journey this file is here for. The TTL travels inside the record, so no
  // section settings are needed on pages that have no wheel.
  function readCode() {
    try {
      var raw = sessionStorage.getItem(STORE_KEY);
      if (!raw) return "";
      var o = JSON.parse(raw);
      if (!o || !o.code || !o.ts) return "";
      var ttlMs = (Number(o.ttl_h) || 24) * 3600 * 1000;
      if (Date.now() - Number(o.ts) > ttlMs) {
        clearCode();
        return "";
      }
      return String(o.code);
    } catch (e) {
      return "";
    }
  }

  function clearCode() {
    try {
      sessionStorage.removeItem(STORE_KEY);
    } catch (e) {}
  }

  // ---- Checkout interception ------------------------------------------------
  var done = false;

  function matchesSelector(node) {
    if (!node || node.nodeType !== 1 || !node.closest) return null;
    var list = SELECTORS.split(",");
    for (var i = 0; i < list.length; i++) {
      var sel = list[i].trim();
      if (!sel) continue;
      try {
        var hit = node.closest(sel);
        if (hit) return hit;
      } catch (e) {}
    }
    return null;
  }

  function onClick(e) {
    if (done) return;

    // composedPath() sees through open shadow roots — UpCart renders its drawer
    // in one, and event.target is retargeted to the host without this.
    var path = typeof e.composedPath === "function" ? e.composedPath() : [e.target];

    var hit = null;
    for (var i = 0; i < path.length && !hit; i++) hit = matchesSelector(path[i]);

    // Class names are not dependable: the same app ships different builds. Dev's
    // drawer button carries `upcart-checkout-button`, production's is
    // `styles_Footer__checkoutButton__<hash>`. Where the button GOES cannot be
    // renamed: Shopify checkout is /checkout or a /cart/c/<token> permalink.
    if (!hit) {
      for (var j = 0; j < path.length; j++) {
        var n = path[j];
        if (!n || n.nodeType !== 1) continue;
        var a = n.tagName === "A" ? n : n.closest ? n.closest("a[href]") : null;
        if (!a) continue;
        var href = a.getAttribute("href") || "";
        if (/\/checkout(\?|#|$|\/)/.test(href) || /\/cart\/c\//.test(href)) {
          hit = a;
          break;
        }
      }
    }
    if (!hit) return;
    redeem(e);
  }

  // Shared tail for both the click and the submit path.
  function redeem(e) {
    var code = readCode();
    if (!code) return; // nothing won, or expired — let checkout proceed normally

    done = true;
    e.preventDefault();
    e.stopPropagation();

    // Cleared before navigating so the code is never silently reapplied to a
    // later, unrelated checkout.
    clearCode();

    window.location.href =
      marketPath("/discount/" + encodeURIComponent(code)) +
      "?redirect=" +
      encodeURIComponent(withParams(marketPath("/checkout")));
  }

  // The theme drawer checks out with <button type="submit" name="checkout"
  // form="CartDrawer-Form">, not an <a href>. A mouse click on it is already
  // caught above, but a submit can also arrive with no click at all — Enter in
  // the form, or a programmatic requestSubmit() — so bind submit as well.
  // `done`, plus the fact that a cancelled click never produces a submit, keeps
  // the two paths from both firing on one checkout.
  function submitIsCheckout(e) {
    var s = e.submitter || document.activeElement;
    if (s && s.nodeType === 1) {
      if (matchesSelector(s)) return true;
      if (s.getAttribute && s.getAttribute("name") === "checkout") return true;
    }
    var form = e.target;
    if (!form || form.nodeType !== 1) return false;
    var act = (form.getAttribute && form.getAttribute("action")) || "";
    if (/\/checkout(\?|#|$|\/)/.test(act)) return true;
    // No identifiable submitter: fall back to the form owning a checkout button.
    // form.elements includes controls attached via the `form` attribute, which is
    // how the drawer's button is wired (it sits outside <form> in .cart__ctas).
    if (!e.submitter && form.elements && form.elements.namedItem) {
      if (form.elements.namedItem("checkout")) return true;
    }
    return false;
  }

  function onSubmit(e) {
    if (done) return;
    if (!submitIsCheckout(e)) return;
    redeem(e);
  }

  // Capture phase so this runs before UpCart's own handler.
  document.addEventListener("click", onClick, true);
  document.addEventListener("submit", onSubmit, true);

  // Also bind on UpCart's own root: composedPath() stops at the host for a
  // CLOSED shadow root, so the document listener alone would never see the click.
  var boundRoot = null;
  var tries = 0;
  function bindRoot() {
    var root = window.upcartDocumentOrShadowRoot;
    if (root && root !== document && root !== boundRoot && root.addEventListener) {
      boundRoot = root;
      root.addEventListener("click", onClick, true);
    }
    if (++tries < 40) setTimeout(bindRoot, 500); // ~20s, covers late drawer init
  }
  bindRoot();

  // Chain onto UpCart's cart-loaded hook when present, without clobbering
  // anything already registered there.
  try {
    if (typeof window.upcartSubscribeCartLoaded === "function") {
      window.upcartSubscribeCartLoaded(bindRoot);
    } else {
      var prev = window.upcartOnCartLoaded;
      window.upcartOnCartLoaded = function () {
        bindRoot();
        if (typeof prev === "function") return prev.apply(this, arguments);
      };
    }
  } catch (e) {}
})();
